import React from 'react';
import { motion } from 'framer-motion';
import { ChartBarIcon, CurrencyDollarIcon, UserGroupIcon, PresentationChartLineIcon } from '@heroicons/react/24/outline';

const ExpertConsultancy = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            {/* Hero Section */}
            <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center bg-gray-900 text-white overflow-hidden">
                <motion.div
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
                    className="absolute inset-0 w-full h-full"
                >
                    <div className="absolute inset-0 bg-blue-900/60 mix-blend-multiply z-10"></div>
                    <img
                        src={require('../../../assets/images/services/expert_consultancy.png')}
                        alt="Expert Consultancy"
                        className="w-full h-full object-cover opacity-60"
                    />
                </motion.div>

                <div className="relative z-20 text-center px-4 max-w-4xl mx-auto">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-6xl font-thin mb-6"
                    >
                        Expert <span className="font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-600">Consultancy</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-lg md:text-xl text-gray-300 font-light max-w-2xl mx-auto"
                    >
                        Strategic Agricultural Intelligence for High-Performance Farming
                    </motion.p>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row gap-16 items-start">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                        className="lg:w-2/3"
                    >
                        <p className="text-gray-600 text-lg leading-relaxed mb-6 font-light">
                            At <span className="font-semibold text-gray-900">Marvelous Agri Solution</span>, consultancy is not advice — it is precision-driven agricultural system design.
                        </p>
                        <p className="text-gray-600 text-lg leading-relaxed mb-6 font-light">
                            Our <span className="font-semibold text-gray-900">Expert Consultancy</span> service transforms traditional farming decisions into data-backed, scientifically structured growth strategies that improve productivity, profitability, and long-term sustainability.
                        </p>
                        <p className="text-xl font-medium text-gray-800 italic border-l-4 border-blue-500 pl-6 py-4 bg-blue-50 rounded-r-2xl mb-10">
                            We act as your strategic agricultural partner — not just a consultant.
                        </p>

                        <div className="bg-white p-8 rounded-2xl shadow-xl border-t-4 border-blue-500 mb-10">
                            <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-2">
                                🚀 Our Consultancy Framework
                            </h3>
                            <div className="space-y-8">
                                <div>
                                    <h4 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                                        🔬 Soil & Crop Intelligence Analysis
                                    </h4>
                                    <p className="text-gray-600 font-light leading-relaxed">
                                        Scientific soil diagnostics, nutrient mapping, and crop-stage assessment to design customized performance plans.
                                    </p>
                                </div>
                                <div>
                                    <h4 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                                        📊 Data-Driven Farm Strategy
                                    </h4>
                                    <p className="text-gray-600 font-light leading-relaxed">
                                        Using field data, climatic patterns, and crop analytics to create measurable and result-oriented cultivation models.
                                    </p>
                                </div>
                                <div>
                                    <h4 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                                        🌱 Integrated Crop Management Planning
                                    </h4>
                                    <p className="text-gray-600 font-light leading-relaxed">
                                        Complete INM, IPM, IDM, and irrigation optimization strategies aligned with crop lifecycle and local conditions.
                                    </p>
                                </div>
                                <div>
                                    <h4 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                                        🛡 Risk Mitigation & Regulatory Guidance
                                    </h4>
                                    <p className="text-gray-600 font-light leading-relaxed">
                                        Helping farmers navigate environmental regulations, sustainable practices, and export-oriented compliance standards.
                                    </p>
                                </div>
                                <div>
                                    <h4 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                                        📈 Profitability Optimization Model
                                    </h4>
                                    <p className="text-gray-600 font-light leading-relaxed">
                                        We focus on increasing yield stability, reducing unnecessary input costs, and improving market value.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-900 p-8 rounded-2xl shadow-xl text-white mb-10">
                            <h3 className="text-2xl font-bold mb-6 flex items-center gap-2 text-blue-400">
                                🌾 From Advisory to Execution
                            </h3>
                            <p className="text-gray-300 text-lg font-light mb-6">
                                Our consultancy goes beyond recommendations. We provide:
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                {[
                                    "Field-level monitoring",
                                    "Preventive pest & disease advisory",
                                    "Resource optimization planning",
                                    "Crop health performance tracking",
                                    "Seasonal strategic planning"
                                ].map((item, index) => (
                                    <div key={index} className="flex items-center gap-3 text-gray-300 font-light">
                                        <div className="bg-blue-500/20 p-1.5 rounded-full flex-shrink-0">
                                            <svg className="h-4 w-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        {item}
                                    </div>
                                ))}
                            </div>
                            <p className="text-lg font-medium text-blue-300 italic px-4 border-l-2 border-blue-500">
                                Every recommendation is practical, measurable, and field-tested.
                            </p>
                        </div>

                        <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 mb-10">
                            <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-2">
                                💡 What Makes Our Consultancy Different?
                            </h3>
                            <ul className="space-y-4">
                                {[
                                    "Customized farm-specific solutions",
                                    "Science-backed decision systems",
                                    "Sustainable & residue-conscious approach",
                                    "Technology-integrated advisory",
                                    "Long-term farmer partnership model"
                                ].map((item, index) => (
                                    <li key={index} className="flex items-center gap-4 text-gray-600 font-light text-lg">
                                        <div className="bg-blue-100 p-1.5 rounded-full flex-shrink-0">
                                            <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-8 rounded-2xl border border-blue-200/60 shadow-sm relative overflow-hidden">
                            <div className="absolute -right-10 -top-10 w-40 h-40 bg-blue-200/40 rounded-full blur-3xl"></div>
                            <h3 className="text-2xl font-bold text-blue-900 mb-4 relative z-10">🌱 Agriculture, Guided by Intelligence</h3>
                            <p className="text-blue-800 text-lg leading-relaxed font-light mb-6 relative z-10">
                                With Marvelous Agri Solution’s Expert Consultancy, farmers gain clarity, confidence, and competitive advantage.
                            </p>
                            <div className="space-y-2 mb-6 relative z-10">
                                <p className="text-blue-800 text-lg font-medium flex items-center gap-2">
                                    <span className="h-2 w-2 bg-blue-500 rounded-full"></span>
                                    We convert complexity into strategy.
                                </p>
                                <p className="text-blue-800 text-lg font-medium flex items-center gap-2">
                                    <span className="h-2 w-2 bg-blue-500 rounded-full"></span>
                                    We convert data into decisions.
                                </p>
                                <p className="text-blue-800 text-lg font-medium flex items-center gap-2">
                                    <span className="h-2 w-2 bg-blue-500 rounded-full"></span>
                                    We convert farms into scalable growth systems.
                                </p>
                            </div>
                            <p className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-indigo-600 relative z-10 pt-4 border-t border-blue-200">
                                Strategic Thinking. Scientific Execution. Sustainable Results.
                            </p>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="lg:w-1/3 sticky top-24"
                    >
                        <div className="bg-white p-2 rounded-2xl shadow-2xl skew-y-2 transform hover:skew-y-0 transition-transform duration-500">
                            <img
                                src={require('../../../assets/images/services/expert_consultancy.png')}
                                alt="Consultancy"
                                className="rounded-xl w-full h-auto object-cover"
                            />
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default ExpertConsultancy;
